import { Dispatch, useRef, useState } from 'react'
import Papa from 'papaparse'
import {
  PiCaretRight,
  PiCheck,
  PiCloudArrowUp,
  PiDownloadSimple,
  PiFileCsv,
  PiTrash,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { ImportData } from 'features/importerv2/importer-types'
import { WizardState, WizardAction } from '../wizardState'
import {
  downloadContactImportTemplate,
  MAX_IMPORT_ROWS,
} from '../contactImportFields'

interface UploadStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
}

/** Format a byte count as a short, human-readable label (e.g. "12 KB", "1.4 MB"). */
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${bytes} B`
}

/**
 * Step 1 of the contact import wizard.
 *
 * Renders a CSV drag-and-drop zone with a "Browse File" button and a
 * "Download CSV template" link. On file selection the CSV is parsed
 * client-side with PapaParse:
 *  - fewer than 2 columns → inline error, no confirmation card
 *  - more than MAX_IMPORT_ROWS rows → inline cap error, no confirmation card
 *  - otherwise the parsed file + rows are stored in wizard state and a
 *    confirmation card is shown.
 *
 * Selecting a file does NOT auto-advance. The user must click
 * "Continue to Column Mapping", which is disabled until a valid file is loaded
 * and dispatches SET_STEP to the map step.
 */
export function UploadStep({ state, dispatch }: UploadStepProps) {
  const [parseError, setParseError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // A valid file is loaded when both the file and its parsed data are in state.
  const uploadedFile = state.file
  const importData = state.importData
  const hasValidFile = uploadedFile != null && importData != null

  /** Parse a CSV File and, on success, store the file + rows in wizard state. */
  function parseFile(file: File) {
    setParseError(null)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const fields = results.meta.fields ?? []

        if (fields.length < 2) {
          setParseError(
            'The file could not be parsed as a valid CSV. Ensure the file uses comma or semicolon delimiters and has at least 2 columns.'
          )
          return
        }

        if (results.data.length === 0) {
          setParseError(
            'This CSV has no data rows. Add at least one contact row below the header and try again.'
          )
          return
        }

        if (results.data.length > MAX_IMPORT_ROWS) {
          setParseError(
            `This file has ${results.data.length.toLocaleString()} rows. The importer currently supports up to ${MAX_IMPORT_ROWS.toLocaleString()} rows — please split it into smaller files.`
          )
          return
        }

        const data: ImportData = {
          rows: results.data as ImportData['rows'],
          headers: fields.map((h) => ({ original: h, mapped: 'ignore' })),
        }

        dispatch({ type: 'SET_FILE', file })
        dispatch({ type: 'SET_IMPORT_DATA', data })
      },
      error: () => {
        setParseError(
          'Failed to parse the CSV file. Please check the file format.'
        )
      },
    })
  }

  function handleFile(file: File | null | undefined) {
    if (!file) {
      return
    }
    parseFile(file)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(false)
    handleFile(e.dataTransfer.files[0])
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(true)
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleFile(e.target.files?.[0])
    e.target.value = ''
  }

  function handleRemove() {
    setParseError(null)
    dispatch({ type: 'RESET' })
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 px-8 py-6 flex flex-col gap-6">
        {/* Drop zone (DS file-upload area) */}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'relative flex cursor-pointer flex-col items-center justify-center gap-6 rounded-xl border-2 border-dashed p-8 transition-all duration-200',
            isDragging
              ? 'border-brand-bright bg-surface-sunken'
              : 'border-ds2026-border-tertiary bg-surface hover:bg-surface-sunken'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => !uploadedFile && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!uploadedFile && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleInputChange}
          />

          <div className="flex flex-col items-center gap-3">
            <PiCloudArrowUp
              className={cn(
                'size-6 transition-colors duration-200',
                isDragging ? 'text-brand-bright' : 'text-fg-secondary'
              )}
            />
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-sm font-medium text-fg">
                Drag &amp; drop or click to upload
              </p>
              <p className="text-xs text-fg-secondary">
                CSV files only · Up to {MAX_IMPORT_ROWS.toLocaleString()} rows
              </p>
            </div>
          </div>

          <FbButton
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              inputRef.current?.click()
            }}
          >
            Browse Files
          </FbButton>
        </div>

        {/* Inline parse / validation error */}
        {parseError && <Alert color="danger" description={parseError} />}

        {/* Uploaded file confirmation card (DS file-item, completed) */}
        {uploadedFile != null && importData != null && (
          <div className="flex items-center justify-between gap-2.5 rounded-xl border border-line bg-surface py-3.5 pl-3 pr-3.5">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-basil-50">
                <PiFileCsv className="size-5 text-brand-active" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="truncate text-sm font-medium text-fg">
                  {uploadedFile.name}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-fg-secondary">
                    {formatFileSize(uploadedFile.size)}
                  </span>
                  <span className="size-1 rounded-full bg-line" />
                  <span className="text-xs font-medium text-success-strong">
                    {importData.rows.length.toLocaleString()} contacts found
                  </span>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* Completed indicator */}
              <span className="flex size-5 items-center justify-center rounded-full bg-brand-active">
                <PiCheck className="size-3 text-white" />
              </span>
              <button
                type="button"
                onClick={handleRemove}
                aria-label="Remove file"
                className="rounded-lg p-1.5 text-fg-tertiary transition-colors hover:bg-surface-sunken hover:text-error-strong"
              >
                <PiTrash className="size-4" />
              </button>
            </div>
          </div>
        )}

        {/* Helper links */}
        <div className="flex items-center justify-end pt-1">
          <button
            type="button"
            onClick={downloadContactImportTemplate}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-bright transition-colors hover:text-brand-active"
          >
            <PiDownloadSimple className="size-4" />
            Download CSV template
          </button>
        </div>
      </div>

      {/* Continue footer */}
      <div className="px-8 py-4 border-t border-line bg-surface flex justify-end">
        <FbButton
          variant="primary"
          disabled={!hasValidFile}
          onClick={() => dispatch({ type: 'SET_STEP', step: 'map' })}
        >
          Continue to Column Mapping
          <PiCaretRight className="size-4" />
        </FbButton>
      </div>
    </div>
  )
}
